import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tab,
  Tabs,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Button,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as AssignIcon,
  SwapHoriz as TransferIcon,
  MoveUp as MoveGroupIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import * as clinicalSupervisorApi from '../../services/clinicalSupervisorApi';
import axios from 'axios';
import AssignClientsModal from './modals/AssignClientsModal';
import TransferClientsModal from './modals/TransferClientsModal';
import ChangeServiceGroupModal from './modals/ChangeServiceGroupModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-mgmt-tabpanel-${index}`}
      aria-labelledby={`client-mgmt-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function ClientManagementView({ token }) {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Service Groups
  const [serviceGroups, setServiceGroups] = useState([]);
  const [selectedServiceGroup, setSelectedServiceGroup] = useState('all');

  // Clients data
  const [clients, setClients] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // Selection
  const [selectedClientIds, setSelectedClientIds] = useState([]);

  // Filters
  const [assignmentFilter, setAssignmentFilter] = useState('all'); // all, assigned, unassigned

  // Modals
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [changeGroupModalOpen, setChangeGroupModalOpen] = useState(false);

  // Load service groups on mount
  useEffect(() => {
    loadServiceGroups();
  }, [token]);

  // Load clients when filters change
  useEffect(() => {
    loadClients();
  }, [selectedServiceGroup, page, rowsPerPage, searchQuery, assignmentFilter, tabValue, token]);

  const loadServiceGroups = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/service-groups`, {
        headers: { 'x-auth-token': token }
      });
      setServiceGroups(response.data.data || []);
    } catch (err) {
      console.error('Error loading service groups:', err);
      setError('Failed to load service groups');
    }
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery || undefined
      };

      // Add assignment filter
      if (assignmentFilter === 'assigned') {
        params.assignedOnly = 'true';
      } else if (assignmentFilter === 'unassigned') {
        params.unassignedOnly = 'true';
      }

      let response;
      if (selectedServiceGroup === 'all') {
        // Load clients from all supervisor's service groups
        // Don't pass unassignedOnly/assignedOnly - let assignmentFilter handle it
        response = await clinicalSupervisorApi.getUnassignedClients(
          params, // Params already has assignedOnly/unassignedOnly from filter
          token
        );
      } else {
        // Load clients from specific service group
        response = await clinicalSupervisorApi.getClientsInServiceGroup(
          selectedServiceGroup,
          params,
          token
        );
      }

      setClients(response.clients || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError(err.response?.data?.msg || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedClientIds(clients.map(c => c._id));
    } else {
      setSelectedClientIds([]);
    }
  };

  const handleSelectOne = (clientId) => {
    setSelectedClientIds(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const handleViewClient = (clientId, therapistId) => {
    if (therapistId) {
      navigate(`/clinical-supervisor/therapists/${therapistId}/clients/${clientId}`);
    } else {
      // If no therapist assigned, navigate to a read-only client view
      navigate(`/clinical-supervisor/clients/${clientId}`);
    }
  };

  const handleRefresh = () => {
    setSelectedClientIds([]);
    loadClients();
  };

  const handleModalSuccess = (message) => {
    setSuccess(message);
    setSelectedClientIds([]);
    loadClients();
  };

  const getServiceGroupName = (client) => {
    const activeGroup = client.serviceGroups?.find(sg => sg.isActive && sg.roles?.includes('client'));
    return activeGroup?.groupId?.name || 'Unknown';
  };

  const getSelectedClientsObjects = () => {
    return clients.filter(c => selectedClientIds.includes(c._id));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Client Management</Typography>
        <IconButton onClick={handleRefresh} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Total Clients</Typography>
            <Typography variant="h4">{totalCount}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Selected</Typography>
            <Typography variant="h4" color="primary.main">{selectedClientIds.length}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Service Groups</Typography>
            <Typography variant="h4">{serviceGroups.length}</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tabs for different operations */}
      <Paper>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="All Clients" />
          <Tab label="Assign to Therapist" />
          <Tab label="Transfer Between Therapists" />
          <Tab label="Change Service Group" />
        </Tabs>

        {/* Filters */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Service Group</InputLabel>
            <Select
              value={selectedServiceGroup}
              onChange={(e) => {
                setSelectedServiceGroup(e.target.value);
                setPage(0);
              }}
              label="Service Group"
            >
              <MenuItem value="all">All Service Groups</MenuItem>
              {serviceGroups.map(sg => (
                <MenuItem key={sg._id} value={sg._id}>{sg.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Assignment Status</InputLabel>
            <Select
              value={assignmentFilter}
              onChange={(e) => {
                setAssignmentFilter(e.target.value);
                setPage(0);
              }}
              label="Assignment Status"
            >
              <MenuItem value="all">All Clients</MenuItem>
              <MenuItem value="assigned">Assigned Only</MenuItem>
              <MenuItem value="unassigned">Unassigned Only</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Search clients"
            placeholder="Name or email..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Clients Table */}
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedClientIds.length > 0 && selectedClientIds.length < clients.length}
                    checked={clients.length > 0 && selectedClientIds.length === clients.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Service Group</TableCell>
                <TableCell>Assigned Therapist</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {searchQuery ? 'No clients found matching search' : 'No clients found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow
                    key={client._id}
                    hover
                    onClick={() => handleSelectOne(client._id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedClientIds.includes(client._id)} />
                    </TableCell>
                    <TableCell>{client.name}</TableCell>
                    <TableCell>{client.email}</TableCell>
                    <TableCell>{getServiceGroupName(client)}</TableCell>
                    <TableCell>
                      {client.assignedTherapist ? (
                        <Chip
                          label={client.assignedTherapist.name}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Chip label="Unassigned" size="small" color="warning" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={client.status}
                        size="small"
                        color={client.status === 'Active' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Client">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewClient(client._id, client.assignedTherapist?._id);
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />

        {/* Action Panels */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="body2" color="text.secondary">
            Select clients from the table above to perform bulk operations. Switch to other tabs to assign, transfer, or move clients between service groups.
          </Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Assign selected unassigned clients to a therapist within your service groups.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AssignIcon />}
            disabled={selectedClientIds.length === 0}
            onClick={() => setAssignModalOpen(true)}
          >
            Assign {selectedClientIds.length} Client{selectedClientIds.length !== 1 ? 's' : ''}
          </Button>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Transfer selected clients from one therapist to another (can be across service groups if you manage both).
          </Typography>
          <Button
            variant="contained"
            startIcon={<TransferIcon />}
            disabled={selectedClientIds.length === 0}
            onClick={() => setTransferModalOpen(true)}
          >
            Transfer {selectedClientIds.length} Client{selectedClientIds.length !== 1 ? 's' : ''}
          </Button>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Move selected clients to a different service group. System will check if current therapists are in the target group.
          </Typography>
          <Button
            variant="contained"
            startIcon={<MoveGroupIcon />}
            disabled={selectedClientIds.length === 0}
            onClick={() => setChangeGroupModalOpen(true)}
          >
            Move {selectedClientIds.length} Client{selectedClientIds.length !== 1 ? 's' : ''} to Group
          </Button>
        </TabPanel>
      </Paper>

      {/* Modals */}
      <AssignClientsModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        selectedClients={getSelectedClientsObjects()}
        serviceGroups={serviceGroups}
        token={token}
        onSuccess={handleModalSuccess}
      />

      <TransferClientsModal
        open={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        selectedClients={getSelectedClientsObjects()}
        serviceGroups={serviceGroups}
        token={token}
        onSuccess={handleModalSuccess}
      />

      <ChangeServiceGroupModal
        open={changeGroupModalOpen}
        onClose={() => setChangeGroupModalOpen(false)}
        selectedClients={getSelectedClientsObjects()}
        serviceGroups={serviceGroups}
        token={token}
        onSuccess={handleModalSuccess}
      />
    </Box>
  );
}

export default ClientManagementView;
